// code-guardian-ignore-duplicates: parallel master-page shell (tab kept in ?tab=, selectTab, the StoreMasterPage tab strip + action slot) copied on purpose so every tabbed settings page matches
import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import TableCardWithStates from '../components/TableCardWithStates';
import { InlineLoader } from '../components/loading';
import ModalFormFooterActions from '../components/ModalFormFooterActions';
import { PageErrorBanner } from '../components/PageErrorBanner';
import { ModalFormErrorBanner } from '../components/ModalFormErrorBanner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Plus, ChevronDown, ChevronRight, ExternalLink, Users, Check, X, Search, CalendarClock, Flag } from 'lucide-react';
import Modal from '../components/Modal';
import RedFlagWarningsTab from '../components/mailers/RedFlagWarningsTab';
import { mailersAPI, distributionListsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

/**
 * Settings → Mailer Setup.
 *
 * Two kinds of mail live here, as two tabs of one page:
 *  - "Scheduled reports" — report mails sent on a schedule to typed-in addresses. This is
 *    the page as it always was (`ScheduledReportsPanel` below), unchanged apart from its
 *    heading and its "New Mailer" button now sitting in this shell.
 *  - "Red flag warnings" — mails the system sends to an employee when they receive a red
 *    flag (components/mailers/RedFlagWarningsTab).
 *
 * The shell follows StoreMasterPage: the tab lives in `?tab=`, the strip and the active
 * tab's primary button share one row (each tab portals its own button into the slot), and
 * a tab is mounted only while it is open, so the one not in view fetches nothing.
 */

type MailerTab = 'scheduled-reports' | 'red-flag-warnings';

const TABS: { key: MailerTab; label: string; icon: typeof Mail; blurb: string }[] = [
  {
    key: 'scheduled-reports',
    label: 'Scheduled reports',
    icon: CalendarClock,
    blurb: 'Schedule recurring email reports — task responses today, more data sources coming.',
  },
  {
    key: 'red-flag-warnings',
    label: 'Red flag warnings',
    icon: Flag,
    blurb:
      'Mails the system sends to an employee when they receive a red flag — a different message for each red flag number, with the details of why, and a record of every mail sent.',
  },
];

export default function MailerSetupPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<MailerTab>(
    TABS.some((t) => t.key === fromUrl) ? (fromUrl as MailerTab) : 'scheduled-reports',
  );

  // A callback ref (not useRef): the panels must re-render once the node exists so they
  // can portal their button into it.
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  const selectTab = (tab: MailerTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-brand" />
          Mailer Setup
        </h1>
        <p className="text-gray-500 text-sm mt-1">{current.blurb}</p>
      </div>

      {/* Tabs left, the active tab's action right — the StoreMasterPage strip. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        <div ref={setActionSlot} className="flex items-center gap-3" />
      </div>

      {activeTab === 'scheduled-reports' && <ScheduledReportsPanel actionSlot={actionSlot} />}
      {activeTab === 'red-flag-warnings' && <RedFlagWarningsTab actionSlot={actionSlot} />}
    </div>
  );
}

interface MailerListItem {
  id: number;
  project_id: number;
  name: string;
  description: string | null;
  data_source: string;
  output_format: string;
  schedule_frequency: 'daily' | 'weekly' | 'monthly';
  schedule_time: string;
  schedule_timezone: string;
  schedule_days_of_week: number[] | null;
  schedule_day_of_month: number | null;
  is_active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  consecutive_failures: number;
  inline_recipient_count: number;
  list_recipient_count: number;
  distribution_list_id: number | null;
}

interface DistributionListLite {
  id: number;
  name: string;
}

interface TaskLite {
  id: string;
  title: string;
  submission_mode: string;
}

interface DataSourceMeta {
  key: string;
  label: string;
  description: string;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DATE_RANGES = [
  { value: 'previous_day', label: 'Previous day' },
  { value: 'previous_week', label: 'Previous week (Mon–Sun)' },
  { value: 'previous_month', label: 'Previous month' },
  { value: 'month_to_date', label: 'Month to date' },
  { value: 'week_to_date', label: 'Week to date (since Mon)' },
  { value: 'last_n_days', label: 'Last N days' },
  { value: 'custom', label: 'Custom (pick dates)' },
];

const parseAddresses = (raw: string): string[] =>
  raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);

const formatIst = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
};

const statusBadge = (status: string | null) => {
  if (!status) return <span className="text-gray-400 text-xs">—</span>;
  const styles: Record<string, string> = {
    success: 'bg-green-50 text-green-700 border-green-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    skipped_empty: 'bg-gray-50 text-gray-600 border-gray-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${styles[status] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  );
};

const blankForm = () => ({
  name: '',
  description: '',
  data_source: 'task_responses',
  task_ids: [] as string[],
  date_range: 'previous_day',
  n_days: 7,
  custom_start_date: '',
  custom_end_date: '',
  grooming_status: 'all' as 'all' | 'clean' | 'unclean',
  dynamic_report_ids: [] as number[],
  output_format: 'csv',
  subject_template: '{project_name} — Report ({date_range})',
  body_template:
    'Hi team,\n\nFind attached the scheduled report for **{date_range}**.\n\n- Project: {project_name}\n- Rows: {row_count}\n\nLet us know if you need anything else.',
  send_if_empty: false,
  distribution_list_id: null as number | null,
  recipients_to: '',
  recipients_cc: '',
  recipients_bcc: '',
  schedule_frequency: 'daily' as 'daily' | 'weekly' | 'monthly',
  schedule_days_of_week: [1, 2, 3, 4, 5] as number[],
  schedule_day_of_month: 1,
  schedule_time: '08:30',
  schedule_timezone: 'Asia/Kolkata',
  is_active: true,
});

type FormState = ReturnType<typeof blankForm>;

// The "Scheduled reports" tab: the Mailer Setup page as it was before the tabs. Its
// heading now belongs to the shell and its "New Mailer" button is portalled into the
// shell's tab row; nothing else about it changed.
function ScheduledReportsPanel({ actionSlot }: { actionSlot: HTMLElement | null }) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);

  const [mailers, setMailers] = useState<MailerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [distributionLists, setDistributionLists] = useState<DistributionListLite[]>([]);
  const [tasks, setTasks] = useState<TaskLite[]>([]);
  const [dataSources, setDataSources] = useState<DataSourceMeta[]>([]);
  const [customReports, setCustomReports] = useState<Array<{ id: number; title: string; description: string | null; context_id: string | null }>>([]);
  const [advancedRecipientsOpen, setAdvancedRecipientsOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<MailerListItem | null>(null);
  const [form, setForm] = useState<FormState>(blankForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [externalConfirm, setExternalConfirm] = useState<{
    externals: string[];
    pendingPayload: any;
  } | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState<MailerListItem | null>(null);
  const [testSendOpen, setTestSendOpen] = useState<MailerListItem | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<string | null>(null);

  const [historyMailer, setHistoryMailer] = useState<MailerListItem | null>(null);
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [recipientsViewMailer, setRecipientsViewMailer] = useState<MailerListItem | null>(null);
  const [recipientsViewData, setRecipientsViewData] = useState<{
    to: Array<{ address: string; source: 'list' | 'inline' }>;
    cc: Array<{ address: string; source: 'list' | 'inline' }>;
    bcc: Array<{ address: string; source: 'list' | 'inline' }>;
  } | null>(null);
  const [recipientsViewLoading, setRecipientsViewLoading] = useState(false);

  const fetchMailers = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const resp = await mailersAPI.list(selectedProjectId);
      if (resp.data?.success) setMailers(resp.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch mailers');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  const fetchSupport = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const [dl, tk, ds] = await Promise.all([
        distributionListsAPI.list(selectedProjectId),
        mailersAPI.tasksForProject(selectedProjectId),
        mailersAPI.dataSources(),
      ]);
      if (dl.data?.success) setDistributionLists(dl.data.data || []);
      if (tk.data?.success) setTasks(tk.data.data || []);
      if (ds.data?.success) setDataSources(ds.data.data || []);
    } catch (err) {
      console.warn('Failed to fetch support data', err);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchMailers();
    fetchSupport();
  }, [fetchMailers, fetchSupport]);

  // Load custom reports for the currently selected data source. For task_responses
  // we scope by the selected task_ids so the picker shows only relevant saved
  // reports (plus any source-wide ones with NULL context_id).
  useEffect(() => {
    if (!selectedProjectId || !form.data_source) {
      setCustomReports([]);
      return;
    }
    let cancelled = false;
    const taskIds =
      form.data_source === 'task_responses' && form.task_ids.length > 0 ? form.task_ids : undefined;
    mailersAPI
      .customReportsForSource(form.data_source, selectedProjectId, taskIds)
      .then((r) => {
        if (cancelled) return;
        setCustomReports(r.data?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setCustomReports([]);
      });
    return () => { cancelled = true; };
  }, [selectedProjectId, form.data_source, form.task_ids.join(',')]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setFormError(null);
    setAdvancedRecipientsOpen(false);
    setIsModalOpen(true);
  };

  const openEdit = async (m: MailerListItem) => {
    try {
      const full = await mailersAPI.get(m.id);
      const f = full.data?.data;
      if (!f) return;
      setEditing(m);
      setForm({
        name: f.name,
        description: f.description ?? '',
        data_source: f.data_source,
        task_ids: (f.data_source_config?.task_ids as string[]) ?? [],
        date_range: f.data_source_config?.date_range ?? 'previous_day',
        n_days: f.data_source_config?.n_days ?? 7,
        custom_start_date: (f.data_source_config?.custom_start_date as string) ?? '',
        custom_end_date: (f.data_source_config?.custom_end_date as string) ?? '',
        grooming_status: (f.data_source_config?.grooming_status as 'all' | 'clean' | 'unclean') ?? 'all',
        // Backward compat: legacy mailers stored a single dynamic_report_id; the
        // new schema is an array, default to single-item array when only the
        // legacy field is present.
        dynamic_report_ids: (() => {
          const arr = f.data_source_config?.dynamic_report_ids;
          if (Array.isArray(arr)) return arr.map(Number);
          const legacy = f.data_source_config?.dynamic_report_id;
          return legacy ? [Number(legacy)] : [];
        })(),
        output_format: f.output_format,
        subject_template: f.subject_template,
        body_template: f.body_template ?? '',
        send_if_empty: f.send_if_empty ?? false,
        distribution_list_id: f.distribution_list_id,
        recipients_to: (f.recipients_to ?? []).join(', '),
        recipients_cc: (f.recipients_cc ?? []).join(', '),
        recipients_bcc: (f.recipients_bcc ?? []).join(', '),
        schedule_frequency: f.schedule_frequency,
        schedule_days_of_week: f.schedule_days_of_week ?? [1, 2, 3, 4, 5],
        schedule_day_of_month: f.schedule_day_of_month ?? 1,
        schedule_time: (f.schedule_time || '08:30').toString().slice(0, 5),
        schedule_timezone: f.schedule_timezone ?? 'Asia/Kolkata',
        is_active: f.is_active,
      });
      // Auto-expand Advanced if this mailer already has inline addresses,
      // so the user can see them on edit instead of them being hidden.
      const inlineCount =
        (f.recipients_to?.length ?? 0) + (f.recipients_cc?.length ?? 0) + (f.recipients_bcc?.length ?? 0);
      setAdvancedRecipientsOpen(inlineCount > 0);
      setFormError(null);
      setIsModalOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load mailer');
    }
  };

  const buildPayload = (): any => ({
    project_id: selectedProjectId,
    name: form.name.trim(),
    description: form.description.trim() || null,
    data_source: form.data_source,
    data_source_config: {
      // Each data source picks the keys it needs and ignores the rest, so it's
      // safe to send the union here without per-source branching.
      task_ids: form.task_ids,
      date_range: form.date_range,
      n_days: form.n_days,
      custom_start_date: form.custom_start_date || null,
      custom_end_date: form.custom_end_date || null,
      grooming_status: form.grooming_status,
      dynamic_report_ids: form.dynamic_report_ids,
    },
    output_format: form.output_format,
    subject_template: form.subject_template,
    body_template: form.body_template,
    send_if_empty: form.send_if_empty,
    distribution_list_id: form.distribution_list_id,
    recipients_to: parseAddresses(form.recipients_to),
    recipients_cc: parseAddresses(form.recipients_cc),
    recipients_bcc: parseAddresses(form.recipients_bcc),
    schedule_frequency: form.schedule_frequency,
    schedule_days_of_week:
      form.schedule_frequency === 'weekly' ? form.schedule_days_of_week : null,
    schedule_day_of_month:
      form.schedule_frequency === 'monthly' ? form.schedule_day_of_month : null,
    schedule_time: form.schedule_time,
    schedule_timezone: form.schedule_timezone,
    is_active: form.is_active,
  });

  const validate = (): string | null => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.data_source) return 'Pick a report type';
    if (form.data_source === 'task_responses' && form.task_ids.length === 0) {
      return 'Select at least one task';
    }
    if (form.date_range === 'custom') {
      if (!form.custom_start_date || !form.custom_end_date) {
        return 'Custom date range needs both Start and End dates';
      }
      if (form.custom_start_date > form.custom_end_date) {
        return 'Start date must be on or before End date';
      }
    }
    if (!form.subject_template.trim()) return 'Subject is required';
    if (!form.schedule_time) return 'Schedule time is required';
    if (form.schedule_frequency === 'weekly' && form.schedule_days_of_week.length === 0) {
      return 'Pick at least one day of week';
    }
    if (!form.distribution_list_id) {
      return 'Pick a mail recipient list — manage them under Settings → Mail Recipient Lists';
    }
    return null;
  };

  const allInlineRecipients = useMemo(
    () => [
      ...parseAddresses(form.recipients_to),
      ...parseAddresses(form.recipients_cc),
      ...parseAddresses(form.recipients_bcc),
    ],
    [form.recipients_to, form.recipients_cc, form.recipients_bcc]
  );

  const externalRecipients = useMemo(
    () => allInlineRecipients.filter((a) => !a.toLowerCase().endsWith('@channelplay.in')),
    [allInlineRecipients]
  );

  const submitInternal = async (payload: any) => {
    setSubmitting(true);
    try {
      if (editing) {
        await mailersAPI.update(editing.id, payload);
      } else {
        await mailersAPI.create(payload);
      }
      setIsModalOpen(false);
      setExternalConfirm(null);
      await fetchMailers();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    const payload = buildPayload();
    if (externalRecipients.length > 0) {
      setExternalConfirm({ externals: externalRecipients, pendingPayload: payload });
      return;
    }
    await submitInternal(payload);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await mailersAPI.remove(deleteConfirm.id);
      setDeleteConfirm(null);
      await fetchMailers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleToggle = async (m: MailerListItem) => {
    try {
      await mailersAPI.setActive(m.id, !m.is_active);
      await fetchMailers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Toggle failed');
    }
  };

  const handleRunNow = async (m: MailerListItem) => {
    if (!confirm(`Run "${m.name}" now? This will send the email to real recipients.`)) return;
    try {
      const resp = await mailersAPI.runNow(m.id);
      const status = resp.data?.data?.status ?? 'unknown';
      alert(`Run completed with status: ${status}`);
      await fetchMailers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Run failed');
    }
  };

  const handleDuplicate = async (m: MailerListItem) => {
    try {
      await mailersAPI.duplicate(m.id);
      await fetchMailers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Duplicate failed');
    }
  };

  const handleTestSend = async () => {
    if (!testSendOpen || !testEmail.trim()) return;
    try {
      setTestStatus('Sending...');
      const resp = await mailersAPI.testSend(testSendOpen.id, testEmail.trim());
      setTestStatus(`Result: ${resp.data?.data?.status ?? 'sent'}`);
    } catch (err: any) {
      setTestStatus(`Failed: ${err.response?.data?.message ?? 'unknown error'}`);
    }
  };

  const openHistory = async (m: MailerListItem) => {
    setHistoryMailer(m);
    setHistoryLoading(true);
    try {
      const resp = await mailersAPI.history(m.id, 30);
      setHistoryRows(resp.data?.data || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openRecipientsView = async (m: MailerListItem) => {
    setRecipientsViewMailer(m);
    setRecipientsViewData(null);
    setRecipientsViewLoading(true);
    try {
      // Load the full mailer (for inline arrays) + the distribution list (if any).
      const mailerResp = await mailersAPI.get(m.id);
      const fullMailer = mailerResp.data?.data;
      const inlineTo: string[] = fullMailer?.recipients_to ?? [];
      const inlineCc: string[] = fullMailer?.recipients_cc ?? [];
      const inlineBcc: string[] = fullMailer?.recipients_bcc ?? [];

      let listTo: string[] = [];
      let listCc: string[] = [];
      let listBcc: string[] = [];
      if (m.distribution_list_id) {
        try {
          const listResp = await distributionListsAPI.get(m.distribution_list_id);
          const dl = listResp.data?.data;
          listTo = dl?.recipients_to ?? [];
          listCc = dl?.recipients_cc ?? [];
          listBcc = dl?.recipients_bcc ?? [];
        } catch (err) {
          console.warn('Failed to load distribution list for recipients view', err);
        }
      }

      // Merge + dedupe (case-insensitive); preserve which source each address came from.
      const merge = (listArr: string[], inlineArr: string[]) => {
        const out: Array<{ address: string; source: 'list' | 'inline' }> = [];
        const seen = new Set<string>();
        for (const a of listArr) {
          const t = (a || '').trim();
          if (!t || seen.has(t.toLowerCase())) continue;
          seen.add(t.toLowerCase());
          out.push({ address: t, source: 'list' });
        }
        for (const a of inlineArr) {
          const t = (a || '').trim();
          if (!t || seen.has(t.toLowerCase())) continue;
          seen.add(t.toLowerCase());
          out.push({ address: t, source: 'inline' });
        }
        return out;
      };

      setRecipientsViewData({
        to: merge(listTo, inlineTo),
        cc: merge(listCc, inlineCc),
        bcc: merge(listBcc, inlineBcc),
      });
    } finally {
      setRecipientsViewLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {actionSlot && createPortal(
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 btn-brand text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
        >
          <Plus className="w-4 h-4" /> New Mailer
        </button>,
        actionSlot,
      )}

      {error && <PageErrorBanner message={error} />}

      <TableCardWithStates loading={loading} isEmpty={mailers.length === 0} emptyMessage="No mailers configured yet.">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Schedule</th>
                <th className="text-left px-4 py-3 font-medium">Next Run</th>
                <th className="text-left px-4 py-3 font-medium">Last Run</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Recipients</th>
                <th className="text-center px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mailers.map((m) => {
                const totalRecipients = (m.inline_recipient_count ?? 0) + (m.list_recipient_count ?? 0);
                return (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{m.name}</div>
                      {m.description && <div className="text-xs text-gray-500 mt-0.5">{m.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{m.schedule_frequency}</div>
                      <div className="text-xs text-gray-500">
                        {String(m.schedule_time).slice(0, 5)} {m.schedule_timezone}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatIst(m.next_run_at)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatIst(m.last_run_at)}</td>
                    <td className="px-4 py-3">{statusBadge(m.last_run_status)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {totalRecipients > 0 ? (
                        <button
                          onClick={() => openRecipientsView(m)}
                          className="inline-flex items-center gap-1 text-brand hover:text-blue-700 hover:underline font-medium"
                          title="View recipients"
                        >
                          <Users className="w-3.5 h-3.5" />
                          {totalRecipients}
                        </button>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <TableActionGroup>
                        <TableActionButton action={m.is_active ? 'pause' : 'activate'} label={m.is_active ? 'Pause' : 'Resume'} onClick={() => handleToggle(m)} role="switch" aria-checked={m.is_active} title={m.is_active ? 'Pause' : 'Resume'} />
                        <TableActionButton action="edit" label="Edit" onClick={() => openEdit(m)} title="Edit" />
                        <TableActionButton action="play" label="Run now" onClick={() => handleRunNow(m)} title="Run now" />
                        <TableActionButton action="send" label="Test send" onClick={() => { setTestSendOpen(m); setTestEmail(''); setTestStatus(null); }} title="Test send" />
                        <TableActionButton action="history" label="History" onClick={() => openHistory(m)} title="History" />
                        <TableActionButton action="duplicate" label="Duplicate" onClick={() => handleDuplicate(m)} title="Duplicate" />
                        <TableActionButton action="delete" label="Delete" onClick={() => setDeleteConfirm(m)} title="Delete" />
                      </TableActionGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
      </TableCardWithStates>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Edit Mailer' : 'New Mailer'}
        size="xl"
        footer={(
          <>
            <ModalFormFooterActions
              onCancel={() => setIsModalOpen(false)}
              submitting={submitting}
              editing={editing}
              createLabel="Create Mailer"
              formId="mailer-edit-form"
            />
          </>
        )}
      >
        <form id="mailer-edit-form" onSubmit={handleSubmit} className="space-y-5">
          <Section title="Basics">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Field label="Name">
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
              </Field>
              <Field label="Description">
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" />
              </Field>
            </div>
          </Section>

          <Section title="Data">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Field label="Report type">
                <select
                  value={form.data_source}
                  onChange={(e) => setForm({ ...form, data_source: e.target.value })}
                  className="form-input"
                >
                  {(dataSources.length > 0 ? dataSources : [{ key: 'task_responses', label: 'Task Responses', description: '' }]).map((ds) => (
                    <option key={ds.key} value={ds.key}>{ds.label}</option>
                  ))}
                </select>
                {dataSources.find((d) => d.key === form.data_source)?.description && (
                  <p className="text-xs text-gray-500 mt-1">
                    {dataSources.find((d) => d.key === form.data_source)?.description}
                  </p>
                )}
              </Field>

              <Field label="Date range">
                <select
                  value={form.date_range}
                  onChange={(e) => setForm({ ...form, date_range: e.target.value })}
                  className="form-input"
                >
                  {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>

              {form.data_source === 'task_responses' ? (
                <Field label="Tasks">
                  <MultiSelectDropdown
                    options={tasks.map((t) => ({
                      value: t.id,
                      label: t.title,
                      hint: t.submission_mode,
                    }))}
                    selected={form.task_ids}
                    onChange={(ids) => setForm({ ...form, task_ids: ids })}
                    placeholder="Select tasks…"
                  />
                </Field>
              ) : null}

              <Field label="Reports" className={form.data_source === 'task_responses' ? '' : 'col-span-2'}>
                <MultiSelectDropdown
                  options={customReports.map((r) => ({
                    value: String(r.id),
                    label: r.title,
                    hint: form.data_source === 'task_responses' && r.context_id
                      ? tasks.find((t) => t.id === r.context_id)?.title
                      : undefined,
                  }))}
                  selected={form.dynamic_report_ids.map(String)}
                  onChange={(ids) => setForm({ ...form, dynamic_report_ids: ids.map(Number) })}
                  placeholder="Raw data (no custom reports selected)"
                />
              </Field>

              {form.date_range === 'custom' && (
                <>
                  <Field label="Start date">
                    <input
                      type="date"
                      value={form.custom_start_date}
                      onChange={(e) => setForm({ ...form, custom_start_date: e.target.value })}
                      className="form-input"
                    />
                  </Field>
                  <Field label="End date">
                    <input
                      type="date"
                      value={form.custom_end_date}
                      onChange={(e) => setForm({ ...form, custom_end_date: e.target.value })}
                      className="form-input"
                    />
                  </Field>
                </>
              )}

              {form.date_range === 'last_n_days' && (
                <>
                  <Field label="N days">
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={form.n_days}
                      onChange={(e) => setForm({ ...form, n_days: parseInt(e.target.value) || 1 })}
                      className="form-input"
                    />
                  </Field>
                  <div />
                </>
              )}

              {form.data_source === 'grooming' && (
                <>
                  <Field label="Grooming status filter">
                    <select
                      value={(form as any).grooming_status ?? 'all'}
                      onChange={(e) => setForm({ ...form, grooming_status: e.target.value } as any)}
                      className="form-input"
                    >
                      <option value="all">All</option>
                      <option value="clean">Clean only</option>
                      <option value="unclean">Unclean only</option>
                    </select>
                  </Field>
                  <div />
                </>
              )}

              {form.dynamic_report_ids.length > 0 && form.data_source === 'task_responses' && form.task_ids.length > 1 && (
                <p className="col-span-2 text-xs text-amber-700">
                  Each custom report is scoped to a single task. Only the task(s) those reports belong to will appear in the mail — other selected tasks are ignored.
                </p>
              )}
            </div>
          </Section>

          <Section title="Content">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Field label="Subject" className="col-span-2">
                <input
                  type="text"
                  value={form.subject_template}
                  onChange={(e) => setForm({ ...form, subject_template: e.target.value })}
                  className="form-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available: <code>{'{project_name}'}</code> <code>{'{date}'}</code> <code>{'{date_range}'}</code> <code>{'{row_count}'}</code>
                  {form.data_source === 'task_responses' && <> <code>{'{task_names}'}</code></>}
                </p>
              </Field>
              <Field label="Body (Markdown)" className="col-span-2">
                <textarea
                  rows={6}
                  value={form.body_template}
                  onChange={(e) => setForm({ ...form, body_template: e.target.value })}
                  className="form-input font-mono text-xs"
                />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.send_if_empty}
                  onChange={(e) => setForm({ ...form, send_if_empty: e.target.checked })}
                />
                Send even when there are zero rows
              </label>
              <div />
            </div>
          </Section>

          <Section title="Delivery">
            <div className="grid grid-cols-2 gap-4 items-start">
              {(['csv', 'inline_html'] as const).map((fmt) => {
                const selected = form.output_format === fmt;
                const title = fmt === 'csv' ? 'CSV attachment' : 'Inline HTML table';
                const blurb = fmt === 'csv'
                  ? 'Report is attached as a CSV file. Best for large reports and Excel-friendly workflows.'
                  : 'Data appears as a table inside the email body. Reports with more than 200 rows automatically fall back to a CSV attachment with a note.';
                return (
                  <label
                    key={fmt}
                    className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selected
                        ? 'border-[#1B3A54] bg-[#EDF2F8] ring-1 ring-[#A9BFDD]'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="output_format"
                      value={fmt}
                      checked={selected}
                      onChange={() => setForm({ ...form, output_format: fmt })}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium text-gray-900 text-sm">{title}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{blurb}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section title="Schedule">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Field label="Frequency">
                <select
                  value={form.schedule_frequency}
                  onChange={(e) => setForm({ ...form, schedule_frequency: e.target.value as any })}
                  className="form-input"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
              <Field label="Time (24h)">
                <input
                  type="time"
                  value={form.schedule_time}
                  onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                  className="form-input"
                />
              </Field>
              <Field label="Timezone">
                <input
                  type="text"
                  value={form.schedule_timezone}
                  onChange={(e) => setForm({ ...form, schedule_timezone: e.target.value })}
                  className="form-input"
                />
              </Field>
              {form.schedule_frequency === 'monthly' ? (
                <Field label="Day of month (29-31 → last day)">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.schedule_day_of_month}
                    onChange={(e) => setForm({ ...form, schedule_day_of_month: parseInt(e.target.value) || 1 })}
                    className="form-input"
                  />
                </Field>
              ) : <div />}
              {form.schedule_frequency === 'weekly' && (
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-700 mb-1">Days of week</div>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((d, idx) => (
                      <label key={idx} className="inline-flex items-center gap-1 text-sm bg-gray-100 px-2 py-1 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.schedule_days_of_week.includes(idx)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.schedule_days_of_week, idx]
                              : form.schedule_days_of_week.filter((x) => x !== idx);
                            setForm({ ...form, schedule_days_of_week: next });
                          }}
                        />
                        {d}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Recipients">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Field label="Mail recipient list (required)" className="col-span-2">
                <select
                  value={form.distribution_list_id ?? ''}
                  onChange={(e) => setForm({ ...form, distribution_list_id: e.target.value ? parseInt(e.target.value) : null })}
                  className="form-input"
                >
                  <option value="">— Pick a list —</option>
                  {distributionLists.map((dl) => (
                    <option key={dl.id} value={dl.id}>{dl.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  Manage lists in{' '}
                  <Link
                    to="/settings/distribution-lists"
                    target="_blank"
                    className="inline-flex items-center gap-0.5 text-brand hover:underline"
                  >
                    Settings → Mail Recipient Lists <ExternalLink className="w-3 h-3" />
                  </Link>
                  . Editing a list updates every mailer that uses it.
                </p>
              </Field>

              <div className="col-span-2">
                <button
                  type="button"
                  onClick={() => setAdvancedRecipientsOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  {advancedRecipientsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Advanced — add ad-hoc recipients
                </button>
              </div>

              {advancedRecipientsOpen && (
                <div className="col-span-2 grid grid-cols-2 gap-4 items-start border-l-2 border-gray-200 pl-4 ml-1">
                  <p className="text-xs text-gray-500 col-span-2">
                    These addresses are sent <strong>in addition</strong> to the list members on every run.
                    Use for CCing yourself or one-off addresses without creating a new list.
                  </p>
                  <Field label="TO (inline)" className="col-span-2">
                    <textarea rows={2} value={form.recipients_to} onChange={(e) => setForm({ ...form, recipients_to: e.target.value })} className="form-input font-mono text-xs" />
                  </Field>
                  <Field label="CC">
                    <textarea rows={2} value={form.recipients_cc} onChange={(e) => setForm({ ...form, recipients_cc: e.target.value })} className="form-input font-mono text-xs" />
                  </Field>
                  <Field label="BCC">
                    <textarea rows={2} value={form.recipients_bcc} onChange={(e) => setForm({ ...form, recipients_bcc: e.target.value })} className="form-input font-mono text-xs" />
                  </Field>
                </div>
              )}

              {externalRecipients.length > 0 && (
                <div className="col-span-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded p-3">
                  <strong>{externalRecipients.length} external recipient(s)</strong> detected (ad-hoc):&nbsp;
                  {externalRecipients.join(', ')}
                </div>
              )}
            </div>
          </Section>

          {formError && <ModalFormErrorBanner message={formError} />}
        </form>
      </Modal>

      {/* External recipient confirmation */}
      <Modal
        isOpen={!!externalConfirm}
        onClose={() => setExternalConfirm(null)}
        title="External recipients detected"
        size="md"
      >
        <p className="text-sm text-gray-700 mb-3">
          The following addresses are outside <code>@channelplay.in</code>. This mailer will send company data to them on every run.
        </p>
        <ul className="text-sm bg-amber-50 border border-amber-200 rounded p-3 mb-4 max-h-48 overflow-y-auto">
          {externalConfirm?.externals.map((e) => <li key={e}>{e}</li>)}
        </ul>
        <div className="flex justify-end gap-2">
          <button onClick={() => setExternalConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => externalConfirm && submitInternal(externalConfirm.pendingPayload)}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg"
          >
            Confirm and Save
          </button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete mailer?" size="sm">
        <p className="text-sm text-gray-700">
          This will soft-delete <strong>{deleteConfirm?.name}</strong>. History rows are retained.
        </p>
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg">
            Delete
          </button>
        </div>
      </Modal>

      {/* Test send */}
      <Modal isOpen={!!testSendOpen} onClose={() => setTestSendOpen(null)} title={`Test send: ${testSendOpen?.name ?? ''}`} size="md">
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Sends the mailer to a single address right now. Doesn't advance the schedule.
          </p>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@channelplay.in"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          {testStatus && (
            <div className="text-sm bg-gray-50 border border-gray-200 rounded p-2 font-mono">{testStatus}</div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setTestSendOpen(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
            <button onClick={handleTestSend} className="px-4 py-2 text-sm font-medium text-white btn-brand rounded-lg">
              Send Test
            </button>
          </div>
        </div>
      </Modal>

      {/* History */}
      <Modal isOpen={!!historyMailer} onClose={() => setHistoryMailer(null)} title={`History: ${historyMailer?.name ?? ''}`} size="xl">
        {historyLoading ? (
          <div className="p-6 flex items-center gap-2 text-sm text-gray-500">
            <InlineLoader label="Loading..." />
          </div>
        ) : historyRows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 text-center">No runs yet.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">When</th>
                  <th className="text-left px-3 py-2 font-medium">Trigger</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Rows</th>
                  <th className="text-right px-3 py-2 font-medium">Recipients</th>
                  <th className="text-right px-3 py-2 font-medium">Size</th>
                  <th className="text-right px-3 py-2 font-medium">Duration</th>
                  <th className="text-left px-3 py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {historyRows.map((r) => {
                  const truncate = (s: string, n = 80) => (s.length > n ? s.slice(0, n - 1) + '…' : s);
                  let reasonNode: React.ReactNode = null;
                  let rowBg = '';
                  if (r.status === 'failed') {
                    const msg = (r.error_message || '').toString().trim() || 'Failed without an error message';
                    reasonNode = (
                      <span className="text-red-700" title={msg}>{truncate(msg)}</span>
                    );
                    rowBg = 'bg-red-50/40';
                  } else if (r.status === 'partial') {
                    const failed: string[] = r.recipients_failed || [];
                    const head = failed[0] || 'unknown';
                    reasonNode = (
                      <span className="text-amber-700" title={failed.join(', ')}>
                        Partial: {failed.length} rejected — {head}
                      </span>
                    );
                    rowBg = 'bg-amber-50/40';
                  } else if (r.status === 'skipped_empty') {
                    reasonNode = <span className="text-gray-500">No rows in date range</span>;
                  }
                  return (
                    <tr key={r.id} className={rowBg}>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatIst(r.run_at)}</td>
                      <td className="px-3 py-2 text-gray-700">{r.trigger}</td>
                      <td className="px-3 py-2">{statusBadge(r.status)}</td>
                      <td className="px-3 py-2 text-gray-700 text-right">{r.row_count ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-700 text-right">
                        {r.recipients_count ?? 0}
                        {r.recipients_failed?.length > 0 && (
                          <span className="ml-1 text-red-600 text-xs">−{r.recipients_failed.length}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-right">
                        {r.attachment_size_bytes ? `${(r.attachment_size_bytes / 1024).toFixed(0)} KB` : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-right">{r.duration_ms ?? '—'} ms</td>
                      <td className="px-3 py-2 text-xs max-w-[260px]">{reasonNode}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      {/* Recipients view */}
      <Modal
        isOpen={!!recipientsViewMailer}
        onClose={() => { setRecipientsViewMailer(null); setRecipientsViewData(null); }}
        title={`Recipients: ${recipientsViewMailer?.name ?? ''}`}
        size="lg"
      >
        {recipientsViewLoading || !recipientsViewData ? (
          <div className="p-6 flex items-center gap-2 text-sm text-gray-500">
            <InlineLoader label="Loading..." />
          </div>
        ) : (
          <RecipientsList data={recipientsViewData} />
        )}
      </Modal>
    </div>
  );
}

interface RecipientsListProps {
  data: {
    to: Array<{ address: string; source: 'list' | 'inline' }>;
    cc: Array<{ address: string; source: 'list' | 'inline' }>;
    bcc: Array<{ address: string; source: 'list' | 'inline' }>;
  };
}

function RecipientsList({ data }: RecipientsListProps) {
  const total = data.to.length + data.cc.length + data.bcc.length;
  const fromList = [...data.to, ...data.cc, ...data.bcc].filter((r) => r.source === 'list').length;
  const fromInline = total - fromList;
  if (total === 0) {
    return <p className="text-sm text-gray-500 py-4 text-center">No recipients configured.</p>;
  }
  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-500">
        <strong>{total}</strong> total — {fromList} from the mail recipient list, {fromInline} ad-hoc.
      </div>
      {(['to', 'cc', 'bcc'] as const).map((key) => {
        const arr = data[key];
        if (arr.length === 0) return null;
        return (
          <div key={key}>
            <h4 className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
              {key.toUpperCase()} ({arr.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {arr.map((r) => {
                const external = !r.address.toLowerCase().endsWith('@channelplay.in');
                return (
                  <span
                    key={`${key}-${r.address}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      external
                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }`}
                    title={`${r.source === 'list' ? 'From the mail recipient list' : 'Ad-hoc inline recipient'}${external ? ' · external domain' : ''}`}
                  >
                    {r.address}
                    {r.source === 'inline' && (
                      <span className="text-[10px] text-gray-500 uppercase">ad-hoc</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tiny presentation helpers ────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      {children}
    </div>
  );
}

function Field({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-sm font-medium text-gray-700 mb-1">{label}</div>
      {children}
    </label>
  );
}

interface MultiSelectOption {
  value: string;
  label: string;
  hint?: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

function MultiSelectDropdown({ options, selected, onChange, placeholder }: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close when clicking outside the wrapper.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const labelByValue = useMemo(() => {
    const m = new Map<string, MultiSelectOption>();
    for (const o of options) m.set(o.value, o);
    return m;
  }, [options]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-[42px] flex flex-wrap items-center gap-1.5 border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {selected.length === 0 && (
          <span className="text-gray-400 px-1">{placeholder ?? 'Select…'}</span>
        )}
        {selected.map((id) => {
          const opt = labelByValue.get(id);
          if (!opt) return null;
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 bg-[#EDF2F8] text-[#1B3A54] border border-[#A9BFDD] text-xs px-2 py-0.5 rounded"
            >
              {opt.label}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(id); }}
                className="hover:text-blue-900"
                aria-label={`Remove ${opt.label}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tasks…"
                className="w-full text-sm pl-8 pr-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No matches.</div>
            ) : (
              filtered.map((o) => {
                const isSelected = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggle(o.value)}
                    className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50/40' : ''
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-4 h-4 rounded border ${
                        isSelected ? 'border-[#1B3A54] text-white' : 'border-gray-300 bg-white'
                      }`}
                      style={isSelected ? { backgroundColor: '#1B3A54' } : undefined}
                    >
                      {isSelected && <Check className="w-3 h-3" />}
                    </span>
                    <span className="flex-1 text-gray-900">{o.label}</span>
                    {o.hint && <span className="text-xs text-gray-400">{o.hint}</span>}
                  </button>
                );
              })
            )}
          </div>
          {selected.length > 0 && (
            <div className="border-t border-gray-100 px-2 py-1.5 flex justify-between text-xs">
              <span className="text-gray-500 px-1 py-0.5">{selected.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-brand hover:underline px-1 py-0.5"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
